import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { Plus, Trash2, Upload, RotateCcw, Download, FileUp } from "lucide-react";

const FIELD =
  "w-full border border-white/15 bg-navy px-3 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-brand-blue";
const LABEL = "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500";

function clone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function Text({ label, value, onChange, testid, textarea, rows = 3 }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      {textarea ? (
        <textarea data-testid={testid} rows={rows} className={`${FIELD} resize-none`} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input data-testid={testid} className={FIELD} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="border border-white/10 bg-surface/40 p-6">
      <h3 className="mb-6 font-display text-xl text-white">{title}</h3>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

export default function AdminContent() {
  const { content, refresh } = useContent();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);
  const portraitRef = useRef(null);
  const importRef = useRef(null);

  useEffect(() => {
    if (content) setDraft(clone(content));
  }, [content]);

  if (!draft) return null;

  const upd = (section, key, value) =>
    setDraft((d) => ({ ...d, [section]: { ...d[section], [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", draft);
      await refresh();
      toast.success("Content saved — site updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset ALL content back to the original defaults? This cannot be undone.")) return;
    try {
      const { data } = await api.post("/content/reset");
      setDraft(clone(data));
      await refresh();
      toast.success("Content reset to defaults");
    } catch {
      toast.error("Reset failed");
    }
  };

  const uploadImage = async (file, apply) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      apply(data.url);
      toast.success("Image uploaded — remember to Save");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    }
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(draft, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `elevate-content-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Content exported as JSON");
  };

  const importJson = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== "object" || !parsed.brand) {
        throw new Error("invalid");
      }
      delete parsed._id;
      setDraft(clone(parsed));
      toast.success("Content imported — review, then click Save to publish");
    } catch {
      toast.error("Import failed — not a valid content JSON file");
    }
  };


  // array helpers
  const setArr = (section, key, arr) =>
    setDraft((d) => ({ ...d, [section]: { ...d[section], [key]: arr } }));
  const setTopArr = (key, arr) => setDraft((d) => ({ ...d, [key]: arr }));

  const B = draft.brand, H = draft.home, A = draft.about, C = draft.contact, F = draft.footer;

  return (
    <div className="mt-10 space-y-8" data-testid="admin-content">
      {/* sticky save bar */}
      <div className="sticky top-0 z-20 -mx-6 flex items-center justify-between border-b border-white/10 bg-navy/90 px-6 py-4 backdrop-blur">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Edit all site content</p>
        <div className="flex flex-wrap gap-3">
          <button onClick={exportJson} data-testid="content-export" className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-300 hover:border-brand-blue hover:text-white">
            <Download size={13} /> Export
          </button>
          <input ref={importRef} type="file" accept="application/json,.json" className="hidden" data-testid="content-import-file"
            onChange={(e) => { importJson(e.target.files?.[0]); e.target.value = ""; }} />
          <button onClick={() => importRef.current?.click()} data-testid="content-import" className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-300 hover:border-brand-blue hover:text-white">
            <FileUp size={13} /> Import
          </button>
          <button onClick={resetAll} data-testid="content-reset" className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-300 hover:border-red-500 hover:text-red-400">
            <RotateCcw size={13} /> Reset
          </button>
          <button onClick={save} disabled={saving} data-testid="content-save" className="bg-brand-blue px-6 py-2 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-magenta disabled:opacity-60">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* BRAND + LOGO */}
      <Section title="Brand & Logo">
        <Text label="Company name" testid="content-company-name" value={B.companyName} onChange={(v) => upd("brand", "companyName", v)} />
        <div>
          <label className={LABEL}>Logo</label>
          <div className="flex items-center gap-4">
            {B.logoUrl ? (
              <span className="flex h-16 w-16 items-center justify-center rounded-md bg-white p-2">
                <img src={B.logoUrl} alt="logo" className="h-full w-full object-contain" />
              </span>
            ) : (
              <span className="flex h-16 w-16 items-center justify-center border border-white/15 font-mono text-[10px] text-slate-500">none</span>
            )}
            <input ref={logoRef} type="file" accept="image/*" className="hidden" data-testid="content-logo-file"
              onChange={(e) => uploadImage(e.target.files?.[0], (url) => upd("brand", "logoUrl", url))} />
            <button onClick={() => logoRef.current?.click()} className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-white hover:border-brand-blue">
              <Upload size={13} /> Upload logo
            </button>
          </div>
          <Text label="Or logo image URL" testid="content-logo-url" value={B.logoUrl} onChange={(v) => upd("brand", "logoUrl", v)} />
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <Text label="Contact email" testid="content-brand-email" value={B.email} onChange={(v) => upd("brand", "email", v)} />
          <Text label="Location" testid="content-brand-location" value={B.location} onChange={(v) => upd("brand", "location", v)} />
        </div>
      </Section>

      {/* HOME */}
      <Section title="Home — Hero">
        <Text label="Hero label" value={H.heroLabel} onChange={(v) => upd("home", "heroLabel", v)} />
        <div className="grid gap-5 sm:grid-cols-3">
          <Text label="Headline line 1" value={H.heroLine1} onChange={(v) => upd("home", "heroLine1", v)} />
          <Text label="Headline line 2" value={H.heroLine2} onChange={(v) => upd("home", "heroLine2", v)} />
          <Text label="Highlighted line 3" value={H.heroLine3} onChange={(v) => upd("home", "heroLine3", v)} />
        </div>
        <Text label="Hero subtext" textarea value={H.heroSubtext} onChange={(v) => upd("home", "heroSubtext", v)} />
        <Text label="Marquee words (one per line)" textarea value={(H.marquee || []).join("\n")} onChange={(v) => upd("home", "marquee", v.split("\n").map((s) => s.trim()).filter(Boolean))} />
      </Section>

      <Section title="Home — Approach / Manifesto">
        <Text label="Section label" value={H.manifestoLabel} onChange={(v) => upd("home", "manifestoLabel", v)} />
        <Text label="Heading (before highlight)" textarea rows={2} value={H.manifestoPre} onChange={(v) => upd("home", "manifestoPre", v)} />
        <Text label="Heading highlight (coloured)" value={H.manifestoHighlight} onChange={(v) => upd("home", "manifestoHighlight", v)} />
        <div className="space-y-4">
          <label className={LABEL}>Chapters</label>
          {(H.chapters || []).map((ch, i) => (
            <div key={i} className="grid gap-3 border border-white/10 p-4 sm:grid-cols-[80px_1fr_auto]">
              <input className={FIELD} placeholder="01" value={ch.n} onChange={(e) => { const a = clone(H.chapters); a[i].n = e.target.value; setArr("home", "chapters", a); }} />
              <div className="space-y-2">
                <input className={FIELD} placeholder="Title" value={ch.title} onChange={(e) => { const a = clone(H.chapters); a[i].title = e.target.value; setArr("home", "chapters", a); }} />
                <textarea rows={2} className={`${FIELD} resize-none`} placeholder="Body" value={ch.body} onChange={(e) => { const a = clone(H.chapters); a[i].body = e.target.value; setArr("home", "chapters", a); }} />
              </div>
              <button onClick={() => setArr("home", "chapters", H.chapters.filter((_, x) => x !== i))} className="self-start border border-white/15 p-2 text-slate-400 hover:border-red-500 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={() => setArr("home", "chapters", [...(H.chapters || []), { n: "0" + ((H.chapters?.length || 0) + 1), title: "", body: "" }])} className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-white hover:border-brand-blue"><Plus size={13} /> Add chapter</button>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <Text label="CTA heading (before highlight)" value={H.ctaPre} onChange={(v) => upd("home", "ctaPre", v)} />
          <Text label="CTA highlight (coloured)" value={H.ctaHighlight} onChange={(v) => upd("home", "ctaHighlight", v)} />
        </div>
      </Section>

      {/* ABOUT */}
      <Section title="About — Company">
        <Text label="Section label" value={A.label} onChange={(v) => upd("about", "label", v)} />
        <div className="grid gap-5 sm:grid-cols-3">
          <Text label="Heading line 1" value={A.headingLine1} onChange={(v) => upd("about", "headingLine1", v)} />
          <Text label="Heading line 2" value={A.headingLine2} onChange={(v) => upd("about", "headingLine2", v)} />
          <Text label="Highlighted line 3" value={A.headingLine3} onChange={(v) => upd("about", "headingLine3", v)} />
        </div>
        <Text label="Company paragraph 1" textarea value={A.companyText1} onChange={(v) => upd("about", "companyText1", v)} />
        <Text label="Company paragraph 2" textarea value={A.companyText2} onChange={(v) => upd("about", "companyText2", v)} />
      </Section>

      <Section title="About — Me">
        <Text label="Section label" value={A.meLabel} onChange={(v) => upd("about", "meLabel", v)} />
        <div className="grid gap-5 sm:grid-cols-2">
          <Text label="Heading (before highlight)" value={A.meHeadingPre} onChange={(v) => upd("about", "meHeadingPre", v)} />
          <Text label="Heading highlight (coloured)" value={A.meHeadingHighlight} onChange={(v) => upd("about", "meHeadingHighlight", v)} />
        </div>
        <Text label="Bio paragraph 1" textarea value={A.meText1} onChange={(v) => upd("about", "meText1", v)} />
        <Text label="Bio paragraph 2" textarea value={A.meText2} onChange={(v) => upd("about", "meText2", v)} />
        <div>
          <label className={LABEL}>Portrait image</label>
          <div className="flex items-center gap-4">
            {A.portraitUrl && <img src={A.portraitUrl} alt="portrait" className="h-16 w-14 object-cover" />}
            <input ref={portraitRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => uploadImage(e.target.files?.[0], (url) => upd("about", "portraitUrl", url))} />
            <button onClick={() => portraitRef.current?.click()} className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-white hover:border-brand-blue"><Upload size={13} /> Upload</button>
          </div>
          <Text label="Or portrait URL" value={A.portraitUrl} onChange={(v) => upd("about", "portraitUrl", v)} />
        </div>
        <Text label="Portrait caption" value={A.portraitCaption} onChange={(v) => upd("about", "portraitCaption", v)} />
        <div className="space-y-3">
          <label className={LABEL}>Stats (3 recommended)</label>
          {(A.stats || []).map((s, i) => (
            <div key={i} className="grid gap-3 sm:grid-cols-[120px_1fr_auto]">
              <input className={FIELD} placeholder="20+" value={s.k} onChange={(e) => { const a = clone(A.stats); a[i].k = e.target.value; setArr("about", "stats", a); }} />
              <input className={FIELD} placeholder="Years in pharma" value={s.v} onChange={(e) => { const a = clone(A.stats); a[i].v = e.target.value; setArr("about", "stats", a); }} />
              <button onClick={() => setArr("about", "stats", A.stats.filter((_, x) => x !== i))} className="border border-white/15 p-2 text-slate-400 hover:border-red-500 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={() => setArr("about", "stats", [...(A.stats || []), { k: "", v: "" }])} className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-white hover:border-brand-blue"><Plus size={13} /> Add stat</button>
        </div>
      </Section>

      {/* CONTACT */}
      <Section title="Contact page">
        <Text label="Section label" value={C.label} onChange={(v) => upd("contact", "label", v)} />
        <div className="grid gap-5 sm:grid-cols-3">
          <Text label="Heading line 1" value={C.headingLine1} onChange={(v) => upd("contact", "headingLine1", v)} />
          <Text label="Heading line 2" value={C.headingLine2} onChange={(v) => upd("contact", "headingLine2", v)} />
          <Text label="Highlighted line 3" value={C.headingLine3} onChange={(v) => upd("contact", "headingLine3", v)} />
        </div>
        <Text label="Blurb" textarea value={C.blurb} onChange={(v) => upd("contact", "blurb", v)} />
        <div className="grid gap-5 sm:grid-cols-3">
          <Text label="Email shown" value={C.email} onChange={(v) => upd("contact", "email", v)} />
          <Text label="Location shown" value={C.location} onChange={(v) => upd("contact", "location", v)} />
          <Text label="Response time" value={C.response} onChange={(v) => upd("contact", "response", v)} />
        </div>
      </Section>

      {/* FOOTER */}
      <Section title="Footer">
        <Text label="Footer blurb" textarea value={F.blurb} onChange={(v) => upd("footer", "blurb", v)} />
        <Text label="Footer tagline" value={F.tagline} onChange={(v) => upd("footer", "tagline", v)} />
      </Section>

      {/* COURSES */}
      <Section title={`Courses (${(draft.courses || []).length})`}>
        {(draft.courses || []).map((c, i) => (
          <div key={i} className="space-y-3 border border-white/10 p-5" data-testid={`content-course-${i}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-blue">{c.code || `Course ${i + 1}`}</span>
              <button onClick={() => setTopArr("courses", draft.courses.filter((_, x) => x !== i))} className="border border-white/15 p-2 text-slate-400 hover:border-red-500 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <input className={FIELD} placeholder="id (unique, e.g. sas-clinical)" value={c.id} onChange={(e) => { const a = clone(draft.courses); a[i].id = e.target.value; setTopArr("courses", a); }} />
              <input className={FIELD} placeholder="Code (SAS/01)" value={c.code} onChange={(e) => { const a = clone(draft.courses); a[i].code = e.target.value; setTopArr("courses", a); }} />
              <input className={FIELD} placeholder="Track (SAS / R / SQL)" value={c.track} onChange={(e) => { const a = clone(draft.courses); a[i].track = e.target.value; setTopArr("courses", a); }} />
              <input className={FIELD} placeholder="Title" value={c.title} onChange={(e) => { const a = clone(draft.courses); a[i].title = e.target.value; setTopArr("courses", a); }} />
              <input className={FIELD} placeholder="Level" value={c.level} onChange={(e) => { const a = clone(draft.courses); a[i].level = e.target.value; setTopArr("courses", a); }} />
              <input className={FIELD} placeholder="Duration" value={c.duration} onChange={(e) => { const a = clone(draft.courses); a[i].duration = e.target.value; setTopArr("courses", a); }} />
              <input className={FIELD} placeholder="Format" value={c.format} onChange={(e) => { const a = clone(draft.courses); a[i].format = e.target.value; setTopArr("courses", a); }} />
            </div>
            <textarea rows={2} className={`${FIELD} resize-none`} placeholder="Blurb" value={c.blurb} onChange={(e) => { const a = clone(draft.courses); a[i].blurb = e.target.value; setTopArr("courses", a); }} />
            <textarea rows={3} className={`${FIELD} resize-none`} placeholder="Topics (one per line)" value={(c.topics || []).join("\n")} onChange={(e) => { const a = clone(draft.courses); a[i].topics = e.target.value.split("\n").map((s) => s.trim()).filter(Boolean); setTopArr("courses", a); }} />
          </div>
        ))}
        <button onClick={() => setTopArr("courses", [...(draft.courses || []), { id: `course-${Date.now()}`, code: "", track: "SAS", title: "", level: "", duration: "", format: "Live + Labs", blurb: "", topics: [] }])} data-testid="content-add-course" className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-white hover:border-brand-blue"><Plus size={13} /> Add course</button>
      </Section>

      {/* PRICING */}
      <Section title={`Pricing tiers (${(draft.pricing || []).length})`}>
        {(draft.pricing || []).map((p, i) => (
          <div key={i} className="space-y-3 border border-white/10 p-5">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-magenta">{p.name || `Tier ${i + 1}`}</span>
              <button onClick={() => setTopArr("pricing", draft.pricing.filter((_, x) => x !== i))} className="border border-white/15 p-2 text-slate-400 hover:border-red-500 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <input className={FIELD} placeholder="Name" value={p.name} onChange={(e) => { const a = clone(draft.pricing); a[i].name = e.target.value; setTopArr("pricing", a); }} />
              <input className={FIELD} placeholder="Tagline" value={p.tagline} onChange={(e) => { const a = clone(draft.pricing); a[i].tagline = e.target.value; setTopArr("pricing", a); }} />
              <input className={FIELD} placeholder="Price label (Enquire)" value={p.priceLabel} onChange={(e) => { const a = clone(draft.pricing); a[i].priceLabel = e.target.value; setTopArr("pricing", a); }} />
            </div>
            <textarea rows={4} className={`${FIELD} resize-none`} placeholder="Features (one per line)" value={(p.features || []).join("\n")} onChange={(e) => { const a = clone(draft.pricing); a[i].features = e.target.value.split("\n").map((s) => s.trim()).filter(Boolean); setTopArr("pricing", a); }} />
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={!!p.highlight} onChange={(e) => { const a = clone(draft.pricing); a[i].highlight = e.target.checked; setTopArr("pricing", a); }} />
              Highlight this tier (Most popular)
            </label>
          </div>
        ))}
        <button onClick={() => setTopArr("pricing", [...(draft.pricing || []), { name: "", tagline: "", priceLabel: "Enquire", features: [], highlight: false }])} className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-white hover:border-brand-blue"><Plus size={13} /> Add tier</button>
      </Section>

      <div className="flex justify-end pb-10">
        <button onClick={save} disabled={saving} className="bg-brand-blue px-8 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-magenta disabled:opacity-60">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
